import { useEffect, useState, type ChangeEvent, type FormEvent, type MouseEvent } from 'react';
import { useNavigate } from 'react-router-dom';

import Input from 'components/form/Input';
import Button from 'components/form/Button';
import CheckBox from 'components/form/CheckBox';

import SignupLogo from 'assets/images/signup_logo.png';
import type { CreateUserType, ReadCheckIdType, ReadPointUsersReferralType, ResCreateUserType } from '@/types';
import { readCheckId, readCheckReferral } from '@/api/balancer/read'; // '@/api/balancer/read';
import { SUCCESS } from '@/lib/name-space';
import { createUser } from '@/api/balancer/create';
import useUserStore from '@/store/user';
import Toast from '@/lib/toast';

const CODE = import.meta.env.VITE_CODE;

export default function Signup() {
  // hooks
  const navigate = useNavigate();
  const { setUser, setIds, increasePoint } = useUserStore();

  // state
  const [form, setForm] = useState({
    telegramId: '',
    walletAddress: '',
    referralBy: '',
    agree: false,
  });
  const [isForm, setIsForm] = useState({
    disabled: false,
    checkId: false,
    referral: false,
  });

  const [showToast, setShowToast] = useState(false);
  const [toastMsg, setToastMsg] = useState('');

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;
    if (toastMsg) {
      setShowToast(true);
      timer = setTimeout(() => {
        setShowToast(false);
        setToastMsg('');
      }, 3000);
    }

    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [toastMsg]);

  // method
  const handleChangeForm = (e: ChangeEvent<HTMLInputElement>) => {
    const { value, id } = e.target;
    setForm((prev) => {
      return {
        ...prev,
        [id]: value,
      };
    });
  };

  const handleCheckId = async (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    const readCheckIdType: ReadCheckIdType = {
      id: String(form.telegramId),
      code: CODE,
    };
    const re = await readCheckId(readCheckIdType);
    if (re.data !== null || re.status !== SUCCESS) {
      // modal
      setToastMsg('That Telegram ID is already registered. Try a different one.');
      return;
    }
    checkIsForm('checkId', true);
    setToastMsg('Nice! That Telegram ID is good to go.');
  };

  const handleCheckReferral = async (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    const readPointUsersReferralType: ReadPointUsersReferralType = {
      referralBy: form.referralBy,
      code: CODE,
    };
    const re = await readCheckReferral(readPointUsersReferralType);
    if (re.status !== SUCCESS) {
      // modal
      setToastMsg(re.message ?? 'An error referral occurred.');
      return;
    }

    if (re.data === 0) {
      // 존재 하지 않는 레퍼럴
      // modal
      setToastMsg('Hmm… that referral code doesn’t look right. Please try again.');
      return;
    }
    if (re.data === 2) {
      // 레퍼럴 횟수 초과
      setToastMsg(re.message ?? ' This referral code has reached its limit.');
      return;
    }
    checkIsForm('referral', true);
    setToastMsg('Referral code accepted!');
  };
  const checkIsForm = (type: string, isValue: boolean) => {
    setIsForm((prev) => {
      return {
        ...prev,
        [type]: isValue,
      };
    });
  };
  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!isForm.checkId) {
      // modal
      setToastMsg('Please check ID.');
      return;
    }

    if (form.referralBy && !isForm.referral) {
      setToastMsg('Please check referral Code.');
      return;
    }

    if (isForm.disabled) {
      return;
    }
    const createUserType: CreateUserType = {
      id: form.telegramId,
      address: form.walletAddress,
      referralBy: form.referralBy ? form.referralBy : null,
      code: CODE,
    };
    const re = await createUser(createUserType);
    if (re.status !== SUCCESS) {
      setToastMsg(re.message ?? 'An error sign up occurred.');
      return;
    }
    const data: ResCreateUserType = re.data;
    setUser(form.telegramId, form.walletAddress, data.referralCode);
    setIds(data.userId, data.settingId, data.pointId);
    increasePoint(Number(data.total));
    // 가입 로직 추가
    // 포인트 메인 화면
    // 레퍼럴이 있는 경우 포인트 추가 엿나??
    // 해제
    navigate('/tutorial');
  };

  useEffect(() => {
    const isValid =
      (form.telegramId?.trim() ?? '') !== '' && (form.walletAddress?.trim() ?? '') !== '' && form.agree === true;
    checkIsForm('disabled', !isValid);
  }, [form]);

  return (
    <div className="mx-auto flex min-h-full max-w-[1024px] flex-col items-center bg-[url(assets/images/background.png)] bg-contain px-[16px] pb-[30px] pt-[49px]">
      <img className="aspect-[1.4] max-h-[200px]" src={SignupLogo} alt="signup page logo" />

      {isForm.checkId}
      <form onSubmit={handleSubmit} className="mt-auto w-full">
        <div className="flex flex-col gap-[16px]">
          {/* Telegram ID */}
          <div className="relative">
            <Input
              required
              id="telegramId"
              label="Telegram ID"
              isAsterisk={true}
              className="h-[38px] pr-[120px]"
              value={form.telegramId}
              onChange={(e) => handleChangeForm(e)}
            />
            <div className="absolute bottom-[1.5px] right-[3px] h-[33px]">
              <Button text={'Check Availability'} onClick={handleCheckId} />
            </div>
          </div>
          {/* Wallet Address */}
          <div className="relative">
            <Input
              required
              className="h-[38px]"
              id="walletAddress"
              label="Wallet Address"
              isAsterisk={true}
              value={form.walletAddress}
              onChange={(e) => handleChangeForm(e)}
            />
          </div>
          {/* Referral Code */}
          <div className="relative">
            <Input
              id="referralBy"
              label="Referral Code"
              className="h-[38px] pr-[95px]"
              value={form.referralBy}
              onChange={(e) => handleChangeForm(e)}
            />
            <div className="absolute bottom-[1.5px] right-[3px] h-[33px]">
              <Button text={'Check Code'} onClick={handleCheckReferral} />
            </div>
          </div>
        </div>

        {/* Agree CheckBox */}
        <div className="mt-[17px] flex gap-[6px] self-start pl-[7px]">
          <CheckBox
            agree={form.agree}
            onClick={() => {
              setForm((prev) => {
                return { ...prev, agree: !prev.agree };
              });
            }}
          />
          <span className="text-[12px]">I agree to stake my points for additional rewards.</span>
        </div>

        <button
          type="submit"
          className={`mt-[40px] w-full rounded-xl border-b-4 ${isForm.disabled ? 'border-[#1A1A1A]' : 'border-[#D0C5B3]'}`}
          disabled={isForm.disabled}
        >
          <div className={`rounded-lg border-2 ${isForm.disabled ? 'border-[#434343]' : 'border-[#6C2D1A]'}`}>
            <div
              className={`rounded-sm border-b-2 border-t-2 ${isForm.disabled ? 'border-b-[#6A6A6A] border-t-[#CFCFCF]' : 'border-b-[#AD4827] border-t-[#FFB7A1]'} `}
            >
              <div className="relative flex h-[41px] items-center justify-center">
                <div
                  className={`absolute left-0 top-0 h-1/2 w-full ${isForm.disabled ? 'bg-[#A2A2A2]' : 'bg-[#FF8661]'}`}
                />
                <div
                  className={`absolute bottom-0 left-0 h-1/2 w-full ${isForm.disabled ? 'bg-[#9A9A9A]' : 'bg-[#FF6535]'}`}
                />

                <span
                  style={{ textShadow: '0px 2px 0px rgba(0, 0, 0, 0.3)' }}
                  className="z-10 text-[24px] font-black text-white"
                >
                  SIGN UP
                </span>
              </div>
            </div>
          </div>
        </button>
      </form>
      {showToast && (
        <Toast
          message={toastMsg}
          duration={3} // 초 단위
          onClose={() => setShowToast(false)}
        />
      )}
    </div>
  );
}
